'use client';

import ClearFilters from '@app/plp/components/ClearFilters';
import FilterContainer from '@app/plp/components/FilterContainer';
import NoSearchResult from '@app/plp/components/NoSearchResult';
import type { ProductGridProps } from '@app/plp/components/ProductGrid';
import '@app/plp/components/ProductGrid/ProductGrid.scss';
import {
    getProductsPromotions,
    pushEvent,
    slugToTitle,
} from '@app/plp/components/ProductGrid/utils';
import ProductPagination from '@app/plp/components/ProductPagination';
import { CATEGORIES_ATTRIBUTE } from '@app/plp/components/SearchLayout/utils';
import PriceRangeProvider from '@app/plp/providers';
import {
    denormalizeAlgoliaFilterName,
    getDefaultCategories,
    getMappedBrandName,
    transformHitForProductTile,
    useFeaturedBrand,
    useSearchPage,
    type ProductCategory,
    type ProductHit,
} from '@libs/algolia';
import {
    Events,
    PageSectionAnalyticsTracking,
    createItemsViewEvent,
    dataLayerPush,
    trackProductTileClick,
    trackProductTileItemClick,
    trackViewSearchResultsEvent,
    useAnalyticsContext,
    type TrackProductTileClickProps,
} from '@libs/analytics';
import {
    ExperimentVersion,
    clsx,
    decodeString,
    useUserSession,
    type Product,
    filterActiveAlgoliaPromotions,
    useExperimentValue,
    useScrollToSection,
    sortProductAlgoliaPromotions,
} from '@libs/common-next';
import type { ProductCardProps } from '@petsmart-ui/sparky';
import {
    Grid,
    Heading,
    LayoutContainer,
    ProductCard,
    TextPassage,
} from '@petsmart-ui/sparky';
import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';
import ProductGridSeo from '@app/plp/components/ProductGrid/ProductGridSeo';
import type { EligiblePromotion } from '@libs/common-types';

export const trackingData = (data: object) =>
    trackProductTileItemClick({
        ...data,
    } as TrackProductTileClickProps);

const filterAndSortAlgoliaPromotions = (promotions: EligiblePromotion[]) => {
    const filteredPromotions = filterActiveAlgoliaPromotions({ promotions });
    let sortedPromotions = [] as EligiblePromotion[];
    if (filteredPromotions?.length) {
        sortedPromotions = sortProductAlgoliaPromotions(filteredPromotions);
    }
    return sortedPromotions;
};

export default function ProductGrid({
    products,
    entryId,
    totalResults,
    queryid,
    forceCategoryH1,
    seoMetadata,
}: ProductGridProps) {
    const { storageUserSession: { isRegistered = false } = {} } =
        useUserSession();
    const scrollToSection = useScrollToSection();

    const { items } = useCurrentRefinements();
    const { setSearchStats, getSearchStats } = useAnalyticsContext();
    const isSearchPage = useSearchPage();
    const isFeaturedBrand = useFeaturedBrand();
    const params = useSearchParams();

    const experimentValue = useExperimentValue();

    const [productsWithPromotions, setProductsWithPromotions] =
        useState<Product[]>(products);
    const [maxRateAvailable, setMaxRateAvailable] = useState<number>(0);
    const [productIdsTracked, setProductIdsTracked] = useState<string[]>([]);
    const [productIdsToTrack, setProductIdsToTrack] = useState<Product[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [shouldGenerateSchema, setShouldGenerateSchema] =
        useState<boolean>(true);

    const currentCategories =
        items.filter(
            (item: CurrentRefinementsConnectorParamsItem) =>
                item.label === CATEGORIES_ATTRIBUTE
        )[0] ?? null;
    const noResultWithFilters = items.length > 0 && products.length === 0;
    const noResult = items.length === 0 && products.length === 0;
    const searchWord = params?.get('q');
    const searchTerm = params?.get('sterm');
    const searchType = params?.get('st') ?? 'manual';
    const searchAssist = params?.get('sa');
    const searchAssistTerm = params?.get('sat');

    const searchIsLoaded = useRef<boolean>(false);
    // because Algolia causes a lot of re-rendering, we need to track what products have been sent to data layer so we do not duplicate them
    const onProductTracked = (id: string) => {
        setProductIdsTracked(current => [...current, `${id}_${queryid}`]);
    };

    const onProductIntersect = (product: Product) => {
        if (
            !productIdsTracked.includes(`${product.id}_${queryid}`) &&
            products.map((p: Product) => p.id).includes(product.id)
        ) {
            setProductIdsToTrack(current => [...current, product]);
            onProductTracked(`${product.id}`);
        }
    };

    const trackProductClick = (product: ProductHit) => {
        const hit = transformHitForProductTile(product) as Product;
        trackingData({
            product: hit,
            ...createItemsViewEvent(
                [hit],
                isSearchPage ? 'search' : 'main navigation',
                isSearchPage ? 'search' : 'standard plp',
                getDefaultCategories(product).map(
                    (p: ProductCategory) => p.name.default
                ),
                getSearchStats()?.searchTerm
            ),
        });
    };

    const optimizeProductImage = (src?: string) => {
        const baseImagesURL = 'https://s7d2.scene7.com/is/image/PetSmart';
        if (src?.includes(baseImagesURL)) {
            const base = src.split('?');
            const existingParams = base.slice(1);
            const joiner = existingParams?.length ? '&' : '';
            return [
                base[0],
                '?',
                ...existingParams,
                joiner,
                'fmt=webp&qlt=80',
            ].join('');
        }
        return undefined;
    };

    useEffect(() => {
        const isManualSearch = searchType === 'manual';
        const term = isManualSearch ? searchWord : searchTerm?.trim();

        setSearchStats({
            searchTerm:
                term && term !== '' ? term : getSearchStats()?.searchTerm || '',
            searchResults: totalResults,
            searchType,
        });
    }, [
        searchWord,
        searchTerm,
        totalResults,
        searchType,
        setSearchStats,
        getSearchStats,
    ]);

    useEffect(() => {
        if (productIdsToTrack.length > 0) {
            const sortedProducts = [...productIdsToTrack].sort(
                (a, b) => a.__position - b.__position
            );
            pushEvent(
                sortedProducts,
                isSearchPage,
                currentCategories,
                searchWord ?? undefined
            );
            setProductIdsToTrack([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only need to run when productIdsToTrack changes
    }, [productIdsToTrack]);

    useEffect(() => {
        setProductsWithPromotions(products);
        if (experimentValue === ExperimentVersion.FULL_SOLUTION) {
            return;
        }
        const maxRateProduct = (products.length &&
            products.reduce((prev, current) =>
                (prev?.rating || 0) > (current?.rating || 0) ? prev : current
            )) || { rating: 0 };
        setMaxRateAvailable(maxRateProduct?.rating || 0);
        // this is needed because for some reason the products we get from useHits in the parent cause 3-5 re-renders every time results change
        const abortController = new AbortController();
        const { signal } = abortController;
        const abortController2 = new AbortController();
        const { signal: signal2 } = abortController2;
        getProductsPromotions(
            products,
            (productsToCheck: Product[]) => {
                setProductsWithPromotions(productsToCheck);
            },
            signal,
            signal2,
            isRegistered
        );
        /* eslint consistent-return:off -- Rule for cleanup functions */
        return () => {
            abortController.abort();
            abortController2.abort();
        };
    }, [products, isRegistered, experimentValue]);

    // Sends current number of products in the page
    // for effective dataLayer tracking
    useEffect(() => {
        const handleTotalResultsUpdate = () => {
            window.dispatchEvent(
                new CustomEvent('totalResultsUpdated', {
                    detail: { totalResults },
                })
            );
        };

        window.addEventListener(
            'getTotalResultsUpdated',
            handleTotalResultsUpdate
        );

        return () => {
            window.removeEventListener(
                'getTotalResultsUpdated',
                handleTotalResultsUpdate
            );
        };
    }, [totalResults]);

    useLayoutEffect(() => {
        const { hash } = window.location;
        // #tabs-1 is added to the url by contentful links
        // if it exists in the url, scroll to product-grid
        if (hash === '#tabs-1') {
            scrollToSection('product-grid', 'instant');
        }

        if (!searchWord || searchIsLoaded.current) {
            return;
        }

        const isManualSearch = searchType === 'manual';

        const term = isManualSearch ? searchWord : searchTerm?.trim() ?? '';
        trackViewSearchResultsEvent(
            term,
            totalResults,
            searchType?.trim(),
            searchAssist as string,
            searchAssistTerm as string
        );
        searchIsLoaded.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- there was no dependency array witch caused the event to be sent multiple times
    }, []);

    useEffect(() => {
        setShouldGenerateSchema(Boolean(products?.length && !isSearchPage));
    }, [isSearchPage, products?.length]);

    return (
        <div id="product-grid" ref={containerRef}>
            <LayoutContainer className="product-grid__container">
                <PriceRangeProvider>
                    <FilterContainer
                        maxRateAvailable={maxRateAvailable}
                        pageId={entryId ?? ''}
                    />
                    <ClearFilters hasHeader={false} />
                </PriceRangeProvider>

                {(searchWord || entryId) && products.length > 0 && (
                    <div className="result-text-container">
                        <Heading
                            data-testid="product-grid__heading"
                            className={clsx(
                                'product-grid__heading',
                                isFeaturedBrand &&
                                    'product-grid__heading-brands'
                            )}
                            tagName={forceCategoryH1 ? 'h1' : 'h2'}
                            size="headline"
                        >
                            {searchWord
                                ? `Showing results for “${decodeString(
                                      searchWord
                                  )}”`
                                : `${getMappedBrandName(
                                      denormalizeAlgoliaFilterName(
                                          slugToTitle(
                                              decodeURIComponent(entryId ?? '')
                                          ) ?? ''
                                      )
                                  )}`}
                        </Heading>
                        <span className="product-grid__results-count">
                            (
                            {searchWord
                                ? `${totalResults}`
                                : `${totalResults} results`}
                            )
                        </span>
                    </div>
                )}

                {noResultWithFilters && <NoSearchResult useFilterMessage />}
                {noResult && <NoSearchResult useFilterMessage={false} />}
                {!noResultWithFilters && !noResult ? (
                    <>
                        <Grid
                            className="product-grid"
                            variant="4up"
                            divider
                            gap="md"
                        >
                            {productsWithPromotions.map(
                                ({
                                    id,
                                    objectID,
                                    outOfStock,
                                    rawProductHit,
                                    eligiblePromotions,
                                    ...product
                                }) => (
                                    <Grid.Item
                                        data-productid={id} // We use this one to create the PLP Surrogate-Key response header
                                        data-testid={`sparky-grid-item-${id}`}
                                        key={id || objectID}
                                    >
                                        <PageSectionAnalyticsTracking
                                            sectionId="product-card"
                                            eventCallback={() => {
                                                dataLayerPush({
                                                    event: Events.VIEW_ITEM_LIST,
                                                });
                                            }}
                                            enabled={false}
                                            OnIntersect={() => {
                                                onProductIntersect({
                                                    id,
                                                    objectID,
                                                    ...product,
                                                    rawProductHit,
                                                });
                                            }}
                                        >
                                            <ProductCard
                                                {...(product as ProductCardProps)}
                                                imgSrc={optimizeProductImage(
                                                    product.imgSrc
                                                )}
                                                rating={product.rating || 0}
                                                orientation="responsive"
                                                data-testid="product-card"
                                                onClick={() => {
                                                    if (rawProductHit) {
                                                        trackProductTileClick({
                                                            list: isSearchPage
                                                                ? 'Search Results'
                                                                : 'PLP Page',
                                                            product:
                                                                rawProductHit,
                                                            variant:
                                                                'traditional',
                                                            position:
                                                                rawProductHit.__position,
                                                        });
                                                    }

                                                    trackProductClick(
                                                        rawProductHit
                                                    );
                                                }}
                                                {...(experimentValue ===
                                                    ExperimentVersion.FULL_SOLUTION &&
                                                    eligiblePromotions?.length && {
                                                        promos: filterAndSortAlgoliaPromotions(
                                                            eligiblePromotions
                                                        ).map(
                                                            ({
                                                                promoText,
                                                            }) => ({
                                                                description:
                                                                    promoText,
                                                            })
                                                        ),
                                                    })}
                                                size="lg"
                                                footer={
                                                    outOfStock ? (
                                                        <TextPassage
                                                            data-testid="sparky-card-out-of-stock"
                                                            size="sm"
                                                            className="product-grid__card-footer--out-of-stock"
                                                        >
                                                            Out of stock
                                                        </TextPassage>
                                                    ) : null
                                                }
                                            />
                                        </PageSectionAnalyticsTracking>
                                    </Grid.Item>
                                )
                            )}
                        </Grid>
                        <ProductPagination containerRef={containerRef} />
                    </>
                ) : null}
            </LayoutContainer>

            {shouldGenerateSchema && (
                <ProductGridSeo seoMetadata={seoMetadata} products={products} />
            )}
        </div>
    );
}
